<template>
  <section id="experience" class="xp" :aria-labelledby="headId">
    <div class="container">
      <span ref="ghostEl" class="ghost depth" aria-hidden="true">03</span>

      <header class="xp-head">
        <p class="label">{{ $t("experience.kicker") }}</p>
        <h2 :id="headId" class="h-section">
          {{ $t("experience.titleHead") }}
          <em class="em">{{ $t("experience.titleEm") }}</em>
        </h2>
      </header>

      <ol class="xp-list">
        <li
          v-for="(e, i) in entries"
          :key="e.id"
          v-reveal="i"
          class="xp-row"
          :class="{ 'is-studio': e.id === STUDIO }"
          :style="{ '--reveal-i': i }"
        >
          <img
            v-if="e.iconSrc"
            class="xp-mark"
            :src="e.iconSrc"
            alt=""
            aria-hidden="true"
            width="34"
            height="34"
          />
          <span v-else class="xp-mark xp-mark--none" aria-hidden="true"></span>

          <div class="xp-body">
            <h3 class="xp-role">{{ L(e.role) }}</h3>
            <p class="xp-org">{{ L(e.org) }}</p>
            <p class="xp-summary">{{ L(e.summary) }}</p>

            <ul v-if="lines(e).length" class="xp-points">
              <li v-for="(h, j) in lines(e)" :key="j">{{ h }}</li>
            </ul>

            <!-- The studio is where the projects live, so the row says so and
                 sends the reader down to them rather than leaving the
                 relationship implied. -->
            <a v-if="e.id === STUDIO" class="xp-link" href="#work" @click.prevent="toWork">
              {{ $t("experience.studioLink", { n: projectCount }) }}
              <svg
                viewBox="0 0 24 24"
                width="15"
                height="15"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M12 5v14M5 12l7 7 7-7" />
              </svg>
            </a>
          </div>

          <p class="xp-dates num">{{ dates(e) }}</p>
        </li>
      </ol>
    </div>
  </section>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { registerLayer, DEPTH } from "@/composables/useDepth";
import experience from "@/data/experience.json";
import projects from "@/data/projects.json";

const STUDIO = "kds"; // Uncapped Works, under its original data id

const MONTHS = {
  nl: ["jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"],
  en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
};

const icons = require.context("@/assets/images/org", false, /\.(png|svg)$/);

export default {
  name: "ExperienceSection",
  setup() {
    const { t, locale } = useI18n();
    const ghostEl = ref(null);
    let stopDepth = null;

    const L = (f) => (typeof f === "string" ? f : (f && (f[locale.value] || f.en)) || "");

    const entries = computed(() =>
      experience.map((e) => {
        let iconSrc = "";
        try {
          if (e.icon) iconSrc = icons(`./${e.icon}`);
        } catch (err) {
          iconSrc = ""; // a missing mark falls back to a blank cell, never a broken image
        }
        return { ...e, iconSrc };
      })
    );

    const projectCount = computed(
      () => projects.filter((p) => p.active !== false).length
    );

    /* A date that is still a TODO in the CV source must not reach the page.
       An open end reads as "present" either way, which is true for the roles
       that have one. */
    const fmt = (v) => {
      if (!v || !/^\d{4}-\d{2}$/.test(String(v))) return "";
      const [y, m] = String(v).split("-");
      return `${MONTHS[locale.value === "nl" ? "nl" : "en"][Number(m) - 1]} ${y}`;
    };
    const dates = (e) => {
      const from = fmt(e.start);
      const to = e.current || !fmt(e.end) ? t("experience.present") : fmt(e.end);
      return from ? `${from} – ${to}` : to;
    };

    const lines = (e) => (e.highlights && (e.highlights[locale.value] || e.highlights.en)) || [];

    const toWork = () => {
      const el = document.getElementById("work");
      if (!el) return;
      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      window.scrollTo({
        top: el.getBoundingClientRect().top + window.scrollY - 76,
        behavior: reduce ? "auto" : "smooth",
      });
    };

    onMounted(() => {
      stopDepth = registerLayer(ghostEl.value, { k: DEPTH.GHOST });
    });
    onBeforeUnmount(() => {
      if (stopDepth) stopDepth();
    });

    return {
      ghostEl,
      entries,
      projectCount,
      headId: "xp-head",
      STUDIO,
      L,
      dates,
      lines,
      toWork,
    };
  },
};
</script>

<style scoped>
.xp {
  position: relative;
  padding: var(--s-10) 0 var(--s-9);
}

.xp-head {
  position: relative;
  margin-bottom: var(--s-8);
}
.xp-head .label {
  margin-bottom: var(--s-3);
}
.em {
  font-family: "Fraunces", Georgia, serif;
  font-style: italic;
  font-weight: 500;
  font-optical-sizing: auto;
  letter-spacing: -0.012em;
}

.ghost {
  top: -0.34em;
}
.depth {
  --travel-y: calc(
    (var(--p, 0.5) - 0.5) * (1 - var(--k, 1)) * var(--travel) * var(--depth-scale)
  );
  transform: translate3d(0, var(--travel-y), 0);
}

.xp-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

/* A register, like the ledger below it: one ruled row per role, the mark on
   the left edge, the dates in a fixed column on the right so they line up
   however long a role title runs. */
.xp-row {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) 150px;
  gap: var(--s-5);
  padding: var(--s-6) 0;
  border-top: var(--hair-w) solid var(--hairline);
}
.xp-row:last-child {
  border-bottom: var(--hair-w) solid var(--hairline);
}

.xp-mark {
  width: 34px;
  height: 34px;
  object-fit: contain;
  margin-top: 3px;
}
.xp-mark--none {
  display: block;
}

.xp-role {
  margin: 0;
  font-size: var(--t-lead);
  font-weight: 650;
  letter-spacing: -0.014em;
  color: var(--ink);
}
.xp-org {
  margin: 2px 0 var(--s-3);
  font-size: var(--t-sm);
  color: var(--grey);
}
.xp-summary {
  margin: 0;
  font-size: 15.5px;
  line-height: 1.55;
  max-width: 62ch;
  color: var(--text);
  text-wrap: pretty;
}

.xp-points {
  margin: var(--s-3) 0 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 5px;
  max-width: 62ch;
}
.xp-points li {
  position: relative;
  padding-left: 16px;
  font-size: 14.5px;
  line-height: 1.5;
  color: var(--grey);
}
.xp-points li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.62em;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--accent);
  opacity: 0.55;
}

.xp-dates {
  margin: 4px 0 0;
  font-size: var(--m-value);
  color: var(--quiet);
  text-align: right;
  white-space: nowrap;
}

/* The studio row is the hinge between the record and the work, so it is the
   one row that carries the accent. */
.is-studio .xp-role {
  color: var(--accent);
}
.xp-link {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-top: var(--s-4);
  font-family: var(--font-measure);
  font-size: var(--m-label);
  font-weight: 500;
  letter-spacing: var(--ls-label);
  text-transform: uppercase;
  color: var(--accent);
}
.xp-link svg {
  transition: transform var(--d-2) var(--ease-out);
}
.xp-link:hover svg {
  transform: translateY(3px);
}

@media (max-width: 900px) {
  .xp-row {
    grid-template-columns: 30px minmax(0, 1fr);
    gap: var(--s-4);
  }
  .xp-dates {
    grid-column: 2;
    text-align: left;
    margin-top: var(--s-3);
  }
  .xp-mark {
    width: 30px;
    height: 30px;
  }
}
@media (max-width: 720px) {
  .xp {
    padding: var(--s-8) 0 var(--s-7);
  }
}
</style>
